<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { fetchContactableInboxes } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const showModal = ref(true);
const selectedFlowId = ref(null);
const isEnrolling = ref(false);
const contactInboxes = ref([]);

const flows = computed(() => store.getters['whatsappFlows/getActiveFlows'] || []);

// Helper function to identify WhatsApp inboxes (both Cloud and Twilio)
const isWhatsAppInbox = (inbox) => {
  // WhatsApp Cloud channel
  if (inbox.channel_type === 'Channel::Whatsapp') {
    return true;
  }
  // Twilio WhatsApp channel
  if (inbox.channel_type === 'Channel::TwilioSms' && inbox.medium === 'whatsapp') {
    return true;
  }
  return false;
};

const whatsappInboxes = computed(() =>
  store.getters['inboxes/getInboxes'].filter(isWhatsAppInbox)
);

const contactInboxIds = computed(() =>
  contactInboxes.value.map(inbox => inbox.id).filter(Boolean)
);

const availableFlows = computed(() => {
  if (!contactInboxIds.value.length) {
    return [];
  }

  return flows.value.filter(flow => {
    const inboxId = flow.inbox_id ?? flow.inboxId ?? null;
    if (!inboxId) {
      return false;
    }
    return contactInboxIds.value.includes(inboxId);
  });
});

watch(availableFlows, newFlows => {
  if (!newFlows.some(flow => flow.id === selectedFlowId.value)) {
    selectedFlowId.value = null;
  }
});

const selectedFlow = computed(() => {
  if (!selectedFlowId.value) return null;
  return availableFlows.value.find(f => f.id === selectedFlowId.value);
});

const hasSteps = computed(() => {
  if (!selectedFlow.value) return false;
  return (selectedFlow.value.total_steps || selectedFlow.value.steps_count || 0) > 0;
});

const canEnroll = computed(() => 
  selectedFlowId.value && hasSteps.value && !isEnrolling.value
);

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('whatsappFlows/get'),
      (async () => {
        if (whatsappInboxes.value.length === 0) {
          await store.dispatch('inboxes/get');
        }
      })(),
    ]);

    contactInboxes.value = await fetchContactableInboxes(props.contactId);
  } catch (error) {
    console.error('Failed to load contact inboxes for flow enrollment:', error);
    contactInboxes.value = [];
  }
});

const enrollContact = async () => {
  if (!canEnroll.value) {
    if (!hasSteps.value) {
      useAlert(t('WHATSAPP_FLOWS.ENROLL.NO_STEPS_ERROR'));
    } else if (!selectedFlowId.value) {
      useAlert(t('WHATSAPP_FLOWS.ENROLL.NO_FLOW_SELECTED'));
    }
    return;
  }

  isEnrolling.value = true;
  
  try {
    const enrollmentPayload = {
      flowId: selectedFlowId.value,
      contactId: props.contactId,
      enrollmentData: {
        initial_status: 'active',
      },
    };
    await store.dispatch('whatsappFlows/createEnrollment', enrollmentPayload);
    useAlert(t('WHATSAPP_FLOWS.ENROLL.SUCCESS'));
    closeModal();
  } catch (error) {
    let errorMessage = t('WHATSAPP_FLOWS.ENROLL.ERROR');
    
    // Parse error messages
    if (error?.response?.data?.error) {
      errorMessage = error.response.data.error;
      
      // Provide helpful context for common errors
      if (errorMessage.includes('does not have an inbox')) {
        errorMessage = t('WHATSAPP_FLOWS.ENROLL.ERROR_CONTACT_NOT_CONNECTED');
      } else if (errorMessage.includes('already enrolled')) {
        errorMessage = t('WHATSAPP_FLOWS.ENROLL.ERROR_CONTACT_ALREADY_ENROLLED');
      } else if (errorMessage.includes('Flow has no steps')) {
        errorMessage = t('WHATSAPP_FLOWS.ENROLL.ERROR_FLOW_HAS_NO_STEPS');
      } else if (errorMessage.includes('Flow is not active')) {
        errorMessage = t('WHATSAPP_FLOWS.ENROLL.ERROR_FLOW_NOT_ACTIVE');
      }
    } else if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    useAlert(errorMessage);
  } finally {
    isEnrolling.value = false;
  }
};

const closeModal = () => {
  showModal.value = false;
  emit('close');
};

const cancel = () => {
  closeModal();
};
</script>

<template>
  <woot-modal v-model:show="showModal" :on-close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('WHATSAPP_FLOWS.ENROLL.TITLE')"
        :header-content="$t('WHATSAPP_FLOWS.ENROLL.DESCRIPTION')"
      />

      <form class="flex flex-wrap mx-8" @submit.prevent="enrollContact">
        <div class="w-full">
          <label>
            {{ $t('WHATSAPP_FLOWS.ENROLL.SELECT_FLOW') }}
            <select v-model="selectedFlowId" class="w-full">
              <option :value="null" disabled>
                {{ $t('WHATSAPP_FLOWS.ENROLL.SELECT_FLOW_PLACEHOLDER') }}
              </option>
              <option
                v-for="flow in availableFlows"
                :key="flow.id"
                :value="flow.id"
              >
                {{ flow.name }}
              </option>
            </select>
          </label>
        </div>

        <div class="flex flex-col w-full gap-2 px-0 py-2">
          <div v-if="!canEnroll && selectedFlow" class="text-xs text-red-600">
            <span v-if="!hasSteps">
              ⚠️ {{ $t('WHATSAPP_FLOWS.ENROLL.BUTTON_DISABLED_NO_STEPS') }}
            </span>
          </div>
          
          <div class="flex items-center justify-end w-full gap-2">
            <NextButton 
              faded
              slate
              type="reset"
              :label="$t('WHATSAPP_FLOWS.ENROLL.CANCEL')"
              @click.prevent="cancel"
            />
            <NextButton
              type="submit"
              :label="$t('WHATSAPP_FLOWS.ENROLL.SUBMIT')"
              :disabled="!canEnroll"
              :is-loading="isEnrolling"
            />
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>
